import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

const AuthModal = ({ isOpen, onClose, initialMode = 'login' }) => {
  const [mode, setMode] = useState(initialMode); // 'login' or 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, signup, requestPasswordReset } = useAuth();
  const { t } = useTranslation();

  // Reset form when modal opens or initialMode changes
  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setAcceptedTerms(false);
      setError('');
      setSuccess('');
    }
  }, [isOpen, initialMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setSuccess('');
      setError(t('fillAllFields'));
      return;
    }

    if (mode === 'signup' && !displayName) {
      setSuccess('');
      setError(t('enterName'));
      return;
    }

    if (mode === 'signup' && !acceptedTerms) {
      setSuccess('');
      setError(t('acceptTermsRequired'));
      return;
    }

    try {
      setError('');
      setSuccess('');
      setLoading(true);
      
      if (mode === 'signup') {
        await signup(email, password, displayName);
      } else {
        await login(email, password);
      }
      
      onClose();
    } catch (error) {
      setError(getErrorMessage(error.code));
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setSuccess('');
      setError(t('enterEmailForReset'));
      return;
    }

    try {
      setError('');
      setSuccess('');
      setLoading(true);
      await requestPasswordReset(email);
      setSuccess(t('passwordResetEmailSent'));
    } catch (error) {
      setError(getErrorMessage(error.code));
    }

    setLoading(false);
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return t('emailAlreadyInUse');
      case 'auth/invalid-email':
        return t('invalidEmail');
      case 'auth/weak-password':
        return t('weakPassword');
      case 'auth/user-not-found':
        return t('userNotFound');
      case 'auth/wrong-password':
        return t('wrongPassword');
      case 'auth/too-many-requests':
        return t('tooManyRequests');
      case 'auth/network-request-failed':
        return t('networkError');
      case 'auth/operation-not-allowed':
        return t('authNotEnabled');
      case 'auth/user-disabled':
        return t('userDisabled');
      case 'auth/missing-email':
        return t('enterEmailForReset');
      default:
        console.error('Unhandled auth error:', errorCode);
        return t('signInFailed');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={onClose}>
          ×
        </button>
        
        <div className="auth-modal-header">
          <h2>{mode === 'login' ? t('welcomeBack') : t('joinNow')}</h2>
          <p>{mode === 'login' ? t('signInDescription') : t('signUpDescription')}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {mode === 'signup' && (
            <div className="form-group">
              <label htmlFor="displayName">{t('yourName')}</label>
              <input
                type="text"
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('enterYourName')}
                required
              />
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="email">{t('email')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('enterYourEmail')}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{t('password')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('enterYourPassword')}
              required
            />
          </div>

          {mode === 'login' && (
            <button
              type="button"
              className="auth-forgot-password-btn"
              onClick={handleForgotPassword}
              disabled={loading}
            >
              {t('forgotPassword')}
            </button>
          )}

          {mode === 'signup' && (
            <div className="terms-agreement">
              <div className="terms-checkbox-wrapper">
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="terms-checkbox"
                />
                <label htmlFor="terms-checkbox" className="terms-text">
                  {t('agreeToTerms')}{' '}
                  <a href="https://upsanddowns.life/terms-and-conditions/" target="_blank" rel="noopener noreferrer" className="terms-link">
                    {t('termsAndConditions')}
                  </a>
                  {' '}{t('and')}{' '}
                  <a href="https://upsanddowns.life/privacy-policy/" target="_blank" rel="noopener noreferrer" className="terms-link">
                    {t('privacyPolicy')}
                  </a>
                </label>
              </div>
            </div>
          )}

          {error && <div className="auth-error">{error}</div>}
          {success && <div className="auth-success">{success}</div>}

          <button type="submit" className="auth-submit-btn" disabled={loading}>
            {loading ? t('pleaseWait') : (mode === 'login' ? t('signInButton') : t('createAccount'))}
          </button>
        </form>

        <div className="auth-modal-footer">
          <p>
            {mode === 'login' ? t('dontHaveAccount') : t('alreadyHaveAccount')}
            <button
              type="button"
              className="auth-mode-toggle"
              onClick={() => {
                setMode(mode === 'login' ? 'signup' : 'login');
                setError('');
                setSuccess('');
                setAcceptedTerms(false);
              }}
            >
              {mode === 'login' ? t('signUp') : t('signInLink')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;